import React, { useState } from "react";
import { useToast } from "../context/ToastContext";
import "./ToolPage.css";
import flaskApi from "../services/flaskApi";
import { extractFilenameFromHeader } from "../utils/fileUtils";
import { extractErrorMessage } from "../utils/errorUtils";
import ToolInfoModal from "../components/common/ToolInfoModal";
import InventorySourceSelector from "./shared/InventorySourceSelector";
import ToolPageLayout from "./shared/ToolPageLayout";
import ToolProgressPanel from "./shared/ToolProgressPanel";


function SalesFloorDisplayTool() {
  const [inventorySource, setInventorySource] = useState("upload");
  const [inventoryShopifyConfig, setInventoryShopifyConfig] = useState({
    location: "Main Store",
    vendors: "",
    excludeArchive: true,
    excludeRS: true
  });
  const [inventoryFile, setInventoryFile] = useState(null);
  const [fetchingShopify, setFetchingShopify] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const { addToast } = useToast();
  const [showInfoModal, setShowInfoModal] = useState(false);

  const toolInfo = {
    toolName: "Sales Floor Display",
    description: "Generate a file to help organize the sales floor display based on inventory.",
    steps: [
      "Upload your Inventory file (Excel).",
      "Click 'Generate Display File' to download the result.",
    ],
    templates: [
      { name: "Inventory Template", link: "/templates/download/template_inventory.xlsx" },
    ],
  };

  const guideTips = [
    "Use the latest inventory export so the sales floor organization file is based on current stock.",
    "This tool is best when you need a quick floor-ready view without manually sorting the workbook.",
    "If the file looks incomplete, re-export inventory before rerunning the tool.",
  ];

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (inventorySource === "upload" && !inventoryFile) {
      addToast("Please upload an inventory file.", "error");
      return;
    }

    const maxSize = 100 * 1024 * 1024;
    if (inventorySource === "upload" && inventoryFile && inventoryFile.size > maxSize) {
      addToast("File is too large. Maximum size is 100MB.", "error");
      return;
    }
    
    let finalInventoryFile = inventoryFile;
    
    if (inventorySource === "shopify") {
      setFetchingShopify(true);
      try {
        const excludeTags = [];
        if (inventoryShopifyConfig.excludeArchive) excludeTags.push("Archive");
        if (inventoryShopifyConfig.excludeRS) excludeTags.push("RS");
        
        const vendors = inventoryShopifyConfig.vendors
          ? inventoryShopifyConfig.vendors.split(",").map(v => v.trim()).filter(v => v)
          : [];
          
        const locationNames = inventoryShopifyConfig.location === "All" 
          ? [] 
          : [inventoryShopifyConfig.location];

        const shopifyRes = await flaskApi.post(
          "/shopify/fetch-inventory-excel", 
          { vendors, exclude_tags: excludeTags, location_names: locationNames },
          { responseType: "blob" }
        );
        
        finalInventoryFile = new File([shopifyRes.data], "shopify_inventory.xlsx", {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });
        
      } catch (err) {
        setFetchingShopify(false);
        const errorMessage = await extractErrorMessage(err, "Failed to fetch inventory from Shopify.");
        addToast(errorMessage, "error");
        return;
      }
      setFetchingShopify(false);
    }

    const formData = new FormData();
    formData.append("inventory_file", finalInventoryFile);

    setLoading(true);
    setUploadProgress(0);

    try {
      const response = await flaskApi.post("/product/sales-floor-display", formData, {
        responseType: "blob",
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        },
      });

      if (
        response.data.type === "application/json" ||
        (response.headers["content-type"] &&
          response.headers["content-type"].includes("application/json"))
      ) {
        const text = await response.data.text();
        const json = JSON.parse(text);
        addToast(`Error: ${json.error}`, "error");
        return;
      }

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const contentDisposition = response.headers["content-disposition"];
      const extractedFilename = extractFilenameFromHeader(contentDisposition);
      const filename = extractedFilename || "Sales_Floor_Display.xlsx";

      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();

      addToast("File downloaded successfully.", "success");
    } catch (error) {
      console.error(error);
      const errorMessage = await extractErrorMessage(error, "Error generating sales floor display file.");
      addToast(errorMessage, "error");
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  return (
    <>
      <ToolPageLayout
        title="Sales Floor Display"
        subtitle={toolInfo.description}

        steps={toolInfo.steps}
        templates={toolInfo.templates}
        tips={guideTips}
        onOpenInfo={() => setShowInfoModal(true)}
      >
        <form onSubmit={handleSubmit} className="tool-form">
          <div className="tool-form-main">
            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Inventory Source</h3>
              </div>
              <InventorySourceSelector
                source={inventorySource}
                onSourceChange={setInventorySource}
                uploadFile={inventoryFile}
                onUploadFileChange={setInventoryFile}
                shopifyConfig={inventoryShopifyConfig}
                onShopifyConfigChange={setInventoryShopifyConfig}
              />
            </section>

            <div className="tool-action-stack">
              <button type="submit" className="tool-btn-primary" disabled={loading || fetchingShopify}>
                {loading || fetchingShopify ? "Processing..." : "Generate Display File"}
              </button>

              <ToolProgressPanel
                loading={loading}
                isDownloadingShopify={fetchingShopify}
                uploadProgress={uploadProgress}
                uploadMessage={`Uploading ${inventoryFile ? inventoryFile.name : "inventory file"}...`}
                processingMessage="Generating Sales Floor Display..."
              />
            </div>
          </div>
        </form>
      </ToolPageLayout>

      <ToolInfoModal
        isOpen={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        toolName={toolInfo.toolName}
        description={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
      />
    </>
  );
}


export default SalesFloorDisplayTool;
